/**
 * การแสดงผลระบบเควส (Quest View) ฝั่ง Client
 *
 * - จัดการแท็บเควส (กำลังทำ · รับได้ · สำเร็จแล้ว)
 * - คำนวณความคืบหน้าสำหรับ HUD Quest Tracker และหน้าต่าง Quest Log
 * - pure functions สำหรับตรรกะการกรอง เพื่อให้ทำ unit test ได้ครบถ้วน
 */

import {
  type QuestDef,
  type QuestProgressRecord,
  isQuestAvailable,
  isQuestReadyToClaim,
  getAllQuests,
  getQuest,
  refreshRepeatables,
} from "@wickhaven/shared";

export type QuestTab = "active" | "available" | "completed";

export interface QuestDisplayItem {
  def: QuestDef;
  record?: QuestProgressRecord;
  readyToClaim: boolean;
}

/**
 * กรองและจัดรายการเควสตามแท็บที่เลือก
 */
export function filterQuests(
  allQuests: readonly QuestDef[],
  records: Map<string, QuestProgressRecord>,
  playerLevel: number,
  tab: QuestTab,
  getItemCount?: (itemId: string) => number,
): QuestDisplayItem[] {
  // เควสรายวันที่ทำเสร็จเมื่อวาน กลับมารับได้ใหม่ (เซิร์ฟเวอร์ตัดสินซ้ำอีกชั้นตอนกดรับ)
  refreshRepeatables(records);

  const completedIds = new Set<string>();
  for (const [id, r] of records) {
    if (r.status === "completed") completedIds.add(id);
  }

  const result: QuestDisplayItem[] = [];

  for (const def of allQuests) {
    const rec = records.get(def.id);

    if (tab === "active") {
      if (rec && rec.status === "active") {
        const ready = isQuestReadyToClaim(def, rec.progress, getItemCount);
        result.push({ def, record: rec, readyToClaim: ready });
      }
    } else if (tab === "completed") {
      if (rec && rec.status === "completed") {
        result.push({ def, record: rec, readyToClaim: false });
      }
    } else if (tab === "available") {
      if (!rec && isQuestAvailable(def, playerLevel, completedIds)) {
        result.push({ def, readyToClaim: false });
      }
    }
  }

  // เรียงลำดับ: เควสเนื้อเรื่องก่อน แล้วตามด้วยเลเวล
  return result.sort((a, b) => {
    if (a.def.type === "story" && b.def.type !== "story") return -1;
    if (b.def.type === "story" && a.def.type !== "story") return 1;
    return a.def.minLevel - b.def.minLevel;
  });
}

/**
 * สรุปข้อความความคืบหน้าของเป้าหมาย เช่น "กำจัดสไลม์ใบไม้ 3/5"
 */
export function formatObjectiveProgress(
  obj: QuestDef["objectives"][number],
  record?: QuestProgressRecord,
  getItemCount?: (itemId: string) => number,
): { text: string; done: boolean; current: number; total: number } {
  let current = 0;
  if (obj.type === "collect_item") {
    current = getItemCount ? getItemCount(obj.targetId) : (record?.progress[obj.id] ?? 0);
  } else {
    current = record?.progress[obj.id] ?? 0;
  }

  const done = current >= obj.targetCount;
  return {
    text: `${obj.description} (${Math.min(current, obj.targetCount)}/${obj.targetCount})`,
    done,
    current: Math.min(current, obj.targetCount),
    total: obj.targetCount,
  };
}

/**
 * ป้ายกำกับชนิดเควส
 */
export function questTypeLabel(type: QuestDef["type"]): { text: string; color: string; icon: string } {
  switch (type) {
    case "story":
      return { text: "เนื้อเรื่องหลัก", color: "#f59e0b", icon: "🔥" };
    case "bounty":
      return { text: "ล่าค่าหัว", color: "#ef4444", icon: "⚔️" };
    case "side":
      return { text: "ภารกิจเสริม", color: "#3b82f6", icon: "📜" };
    case "daily":
      return { text: "ภารกิจรายวัน", color: "#10b981", icon: "🌅" };
    case "job":
      return { text: "เลื่อนขั้นอาชีพ", color: "#e879f9", icon: "👑" };
    default:
      return { text: "ภารกิจ", color: "#8b5cf6", icon: "✨" };
  }
}
